import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import type { Organization, OrgMembership } from '../types'
import * as api from '../api/client'
import { useAuth } from './AuthContext'

const CURRENT_ORG_KEY = 'factory_current_org'

// Detect the auto-created personal workspace org by slug pattern
function isPersonalOrg(org: Organization): boolean {
  return org.slug.endsWith('-personal')
}

// Org role hierarchy
export const OrgRoles = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
  VIEWER: 'viewer',
} as const

export type OrgRole = typeof OrgRoles[keyof typeof OrgRoles]

// Permission helpers for org roles
export const isOrgOwner = (role?: string): boolean => role === OrgRoles.OWNER
export const isOrgAdmin = (role?: string): boolean =>
  role === OrgRoles.ADMIN || role === OrgRoles.OWNER
export const isOrgMember = (role?: string): boolean =>
  role === OrgRoles.MEMBER || isOrgAdmin(role)
export const isOrgViewer = (role?: string): boolean =>
  role === OrgRoles.VIEWER || isOrgMember(role)

// Org-level permissions
export const canManageOrgMembers = (role?: string): boolean => isOrgAdmin(role)
export const canCreateTeam = (role?: string): boolean => isOrgAdmin(role)
export const canDeleteOrg = (role?: string): boolean => isOrgOwner(role)
export const canInviteMembers = (role?: string): boolean => isOrgAdmin(role)
export const canCreateOrgProject = (role?: string): boolean => isOrgMember(role)

interface OrgPermissions {
  isOwner: boolean
  isAdmin: boolean
  isMember: boolean
  isViewer: boolean
  canManageMembers: boolean
  canCreateTeam: boolean
  canDeleteOrg: boolean
  canInviteMembers: boolean
  canCreateProject: boolean
}

interface OrgContextType {
  // Current organization state
  currentOrg: Organization | null
  currentMembership: OrgMembership | null
  organizations: Organization[]
  isLoading: boolean

  // Org permissions
  orgPermissions: OrgPermissions

  // Actions
  setCurrentOrg: (org: Organization | null) => void
  refreshOrganizations: () => Promise<void>
  createOrg: (name: string, slug?: string) => Promise<Organization>
  leaveOrg: (orgId: string) => Promise<void>

  // Helper
  isPersonalMode: boolean
}

const OrgContext = createContext<OrgContextType | undefined>(undefined)

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [currentOrg, setCurrentOrgState] = useState<Organization | null>(null)
  const [memberships, setMemberships] = useState<Map<string, OrgMembership>>(new Map())
  const [isLoading, setIsLoading] = useState(true)

  // Load organizations when authenticated
  useEffect(() => {
    const loadOrgs = async () => {
      if (!isAuthenticated) {
        setOrganizations([])
        setCurrentOrgState(null)
        setMemberships(new Map())
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const response = await api.listOrganizations()
        const orgs = response.organizations || []
        setOrganizations(orgs)

        // Try to restore previously selected org from localStorage
        const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY)
        if (savedOrgId) {
          const savedOrg = orgs.find(o => o.id === savedOrgId)
          if (savedOrg) {
            setCurrentOrgState(savedOrg)
          } else {
            // Saved org no longer exists — fall back to personal workspace
            const personalOrg = orgs.find(isPersonalOrg)
            if (personalOrg) {
              setCurrentOrgState(personalOrg)
              localStorage.setItem(CURRENT_ORG_KEY, personalOrg.id)
            }
          }
        } else {
          // No saved org — default to personal workspace
          const personalOrg = orgs.find(isPersonalOrg)
          if (personalOrg) {
            setCurrentOrgState(personalOrg)
            localStorage.setItem(CURRENT_ORG_KEY, personalOrg.id)
          } else if (orgs.length === 1) {
            // Fallback: auto-select the only org
            setCurrentOrgState(orgs[0])
            localStorage.setItem(CURRENT_ORG_KEY, orgs[0].id)
          }
        }

        // Unblock the UI — memberships load in the background
        setIsLoading(false)

        // Load memberships in parallel (non-blocking)
        const membershipPromises = orgs.map(async (org) => {
          try {
            const membersResponse = await api.listOrgMembers(org.id)
            const userMembership = membersResponse.members?.find(m => m.user_id === user?.id)
            return userMembership ? [org.id, userMembership] as const : null
          } catch {
            return null
          }
        })
        const results = await Promise.all(membershipPromises)
        const membershipMap = new Map<string, OrgMembership>()
        for (const result of results) {
          if (result) {
            membershipMap.set(result[0], result[1])
          }
        }
        setMemberships(membershipMap)
      } catch {
        setOrganizations([])
        setIsLoading(false)
      }
    }

    loadOrgs()
  }, [isAuthenticated, user?.id])

  // Get current membership
  const currentMembership = useMemo(() => {
    if (!currentOrg) return null
    return memberships.get(currentOrg.id) || null
  }, [currentOrg, memberships])

  // Set current org and persist to localStorage
  const setCurrentOrg = useCallback((org: Organization | null) => {
    setCurrentOrgState(org)
    if (org) {
      localStorage.setItem(CURRENT_ORG_KEY, org.id)
    } else {
      localStorage.removeItem(CURRENT_ORG_KEY)
    }
  }, [])

  // Refresh organizations list
  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated) return

    try {
      const response = await api.listOrganizations()
      setOrganizations(response.organizations || [])

      // Update memberships
      const membershipMap = new Map<string, OrgMembership>()
      for (const org of response.organizations || []) {
        try {
          const membersResponse = await api.listOrgMembers(org.id)
          const userMembership = membersResponse.members?.find(m => m.user_id === user?.id)
          if (userMembership) {
            membershipMap.set(org.id, userMembership)
          }
        } catch {
          // Ignore errors
        }
      }
      setMemberships(membershipMap)

      // If current org no longer exists, fall back to personal workspace
      if (currentOrg && !response.organizations?.find(o => o.id === currentOrg.id)) {
        const personalOrg = response.organizations?.find(isPersonalOrg)
        setCurrentOrg(personalOrg || null)
      }
    } catch {
      // Ignore errors
    }
  }, [isAuthenticated, user?.id, currentOrg, setCurrentOrg])

  // Create new organization
  const createOrg = useCallback(async (name: string, slug?: string): Promise<Organization> => {
    const org = await api.createOrganization({ name, slug })
    await refreshOrganizations()
    return org
  }, [refreshOrganizations])

  // Leave organization
  const leaveOrg = useCallback(async (orgId: string): Promise<void> => {
    if (!user) return
    await api.removeOrgMember(orgId, user.id)

    // If leaving current org, switch to personal workspace
    if (currentOrg?.id === orgId) {
      const personalOrg = organizations.find(o => isPersonalOrg(o) && o.id !== orgId)
      setCurrentOrg(personalOrg || null)
    }

    await refreshOrganizations()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-lint baseline: deps intentionally omitted
  }, [user, currentOrg, setCurrentOrg, refreshOrganizations])

  // Compute org permissions based on current membership role
  const orgPermissions = useMemo<OrgPermissions>(() => {
    const role = currentMembership?.role
    return {
      isOwner: isOrgOwner(role),
      isAdmin: isOrgAdmin(role),
      isMember: isOrgMember(role),
      isViewer: isOrgViewer(role),
      canManageMembers: canManageOrgMembers(role),
      canCreateTeam: canCreateTeam(role),
      canDeleteOrg: canDeleteOrg(role),
      canInviteMembers: canInviteMembers(role),
      canCreateProject: canCreateOrgProject(role),
    }
  }, [currentMembership?.role])

  // Helper to check if in personal workspace (personal org selected or no org)
  const isPersonalMode = currentOrg === null || isPersonalOrg(currentOrg)

  return (
    <OrgContext.Provider
      value={{
        currentOrg,
        currentMembership,
        organizations,
        isLoading,
        orgPermissions,
        setCurrentOrg,
        refreshOrganizations,
        createOrg,
        leaveOrg,
        isPersonalMode,
      }}
    >
      {children}
    </OrgContext.Provider>
  )
}

export function useOrg() {
  const context = useContext(OrgContext)
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider')
  }
  return context
}
